// charts/HeatmapChart.jsx
import { useTheme } from '@mui/material/styles';
import { BaseHighchart } from './BaseHighchart';

/*
 * HeatmapChart
 * -------------
 * Expects:
 *   data = {
 *     categories: string[],                        // x-axis labels
 *     series: Array<{
 *       label: string,                             // y-axis label for this row
 *       values: number[]                           // values aligned to categories
 *     }>
 *   }
 * Cosmetic props:
 *   - legend: 'right' | 'bottom' | 'top' | null/undefined to hide
 *   - showValues: boolean                         // display value inside each cell
 *   - minColor?: string                            // color for extreme negative values
 *   - maxColor?: string                            // color for extreme positive values
 *   - xLabel?: string
 *   - yLabel?: string
 */

export const HeatmapChart = ({
  data,
  legend,
  showValues = false,
  minColor,
  maxColor,
  displayAsPercentage = false, // NEW
  xLabel,
  yLabel,
  onClick,
}) => {
  const theme = useTheme();
  const dividerColor = theme.palette.divider;
  const labelStyle = { color: theme.palette.text.secondary };

  if (!data || !data.categories?.length || !data.series?.length) return null;

  // Build y categories + heatmap data
  const yCategories = data.series.map((s) => s.label);
  const heatmapData = [];

  let minVal = Infinity;
  let maxVal = -Infinity;

  data.series.forEach((row, yIndex) => {
    row.values.forEach((val, xIndex) => {
      heatmapData.push([xIndex, yIndex, val]);
      if (val < minVal) minVal = val;
      if (val > maxVal) maxVal = val;
    });
  });

  const absMax = Math.max(Math.abs(minVal), Math.abs(maxVal));

  const legendOptions =
    legend == null ? { enabled: false }
    : legend === 'bottom' ? { enabled: true, align: 'center', verticalAlign: 'bottom' }
    : legend === 'top' ? { enabled: true, align: 'center', verticalAlign: 'top' }
    : { enabled: true, layout: 'vertical', align: 'right', verticalAlign: 'middle' };

  const chartSpacing =
    legend === 'bottom' ? [8, 8, 24, 8]
    : legend === 'top' ? [24, 8, 8, 8]
    : [8, 8, 8, 8];

  const formatValue = (val) => (displayAsPercentage ? `${val.toFixed(1)}%` : val);

  const options = {
    chart: {
      type: 'heatmap',
      spacing: chartSpacing,
      animation: { duration: 300 },
    },
    title: { text: null },
    legend: legendOptions,

    xAxis: {
      categories: data.categories,
      title: { text: xLabel || null, style: labelStyle },
      lineWidth: 1,
      lineColor: dividerColor,
      tickLength: 0,
    },
    yAxis: {
      categories: yCategories,
      labels: {
        formatter: function () {
          return this.value;
        },
      }, // ← add this
      title: { text: yLabel || null, style: labelStyle },
      // reversed: true,  // <-- this can sometimes hide labels
      lineWidth: 1,
      lineColor: dividerColor,
    },
    colorAxis: {
      min: -absMax,
      max: absMax,
      stops: [
        [0.0, '#ff0000'], // red at extreme negative
        [0.4, '#fe1c1cff'], // mid red
        [0.5, '#ffffff'], // white (zero)
        [0.6, '#4dff4d'], // mid green
        [1.0, '#00ff00'], // green at extreme positive
      ],
    },
    // legend: {  /* This is useful for seeing how the color axis is being applied */
    //   enabled: true,
    //   layout: 'vertical',          // vertical bar
    //   align: 'right',              // place it to the right
    //   verticalAlign: 'middle'      // centered vertically
    // },
    tooltip: {
      formatter: function () {
        return `<b>${yCategories[this.point.y]}</b> / <b>${data.categories[this.point.x]}</b>: <b>${formatValue(this.point.value)}</b>`;
      },
    },
    plotOptions: {
      series: {
        dataLabels: {
          enabled: showValues && data.categories.length < 13, // 👈 hide if 13 or more columns
          formatter: function () {
            return formatValue(this.point.value);
          },
          style: {
            color: theme.palette.text.primary,
            fontWeight: 'bold',
            textOutline: 'none',
          },
        },
        turboThreshold: 0,
        borderWidth: 1,
        borderColor: dividerColor,
        cursor: onClick ? 'pointer' : 'default',

        point: {
          events: {
            click() {
              const { x, y, value } = this;
              const category = data.categories[x];
              const ycat = yCategories[y];
              if (onClick) onClick({ category, ycat, value });
            },
          },
        },
      },
    },

    series: [
      {
        name: 'Heatmap',
        borderWidth: 1,
        data: heatmapData,
        borderColor: dividerColor,
      },
    ],
  };

  return <BaseHighchart options={options} />;
};
